import { useNavigate } from 'react-router-dom'
import { datadogRum } from '@datadog/browser-rum'
import { CheckoutRules, CHECKOUT_RULE_DOCUMENT_TYPES } from '../../interfaces'
import type { CheckoutRuleDocumentType } from '../../interfaces'
import type { CheckoutRuleErrorReason } from '../../graphQL/commons'
import { addAlert } from '../../redux/slices/config'
import { clearPlaceOrderActions } from '../../redux/slices/cart'
import {
  clearSelection,
  selectLastAppliedRules,
} from '../../redux/slices/checkout'
import { generateDataDogContext } from '../../utils/dataDog'
import getAlertErrorMessage, {
  AlertMessageType,
} from '../../utils/getAlertErrorMessage'
import resolveCheckoutRuleRoute from '../../utils/checkoutRuleRoute'
import useAppDispatch from '../useAppDispatch'
import useAppSelector from '../useAppSelector'
import useAppStore from '../useAppStore'

const FUNCTION_NAME = 'useCheckoutRuleRecovery'

/** Every value of the rule-type enum, for narrowing an untyped rule name. */
const KNOWN_RULE_TYPES = Object.values(CheckoutRules) as string[]

const isCheckoutRule = (name: string): name is CheckoutRules =>
  KNOWN_RULE_TYPES.includes(name)

/**
 * Narrow a `CheckoutRules` member down to the two document types the slice
 * stores a selection under.
 *
 * `clearSelection` is keyed on `PAYMENT_METHODS` / `DELIVERY_DATES`, so a
 * reason-driven clear has to filter the rule name before dispatching, or a
 * validation rule the engine named reaches a reducer typed for the other two.
 *
 * The source list is imported from `interfaces/config.ts` rather than
 * re-declared here, so an `EveryDocumentTypeListed` third document type does
 * not silently skip the clear — review of #812 (2026-10-06). The reader-side
 * guarantee stays the same shape as `CHECKOUT_RULE_DOCUMENT_TYPES` provides
 * for config authors.
 */
const isDocumentRule = (
  name: CheckoutRules,
): name is CheckoutRuleDocumentType =>
  (CHECKOUT_RULE_DOCUMENT_TYPES as readonly string[]).includes(name)

/**
 * Reports a reason-handled rejection to Datadog.
 *
 * Lifted out of the branches so the three reporting arms carry one shape to
 * RUM — a Datadog alert matching on `reason` sees the same message prefix
 * and the same facet set regardless of which branch fired. The fourth arm
 * (`undefined` / `required_selection_missing`) navigates and reports
 * nothing; a shopper reaching the picker it was sent to is the expected
 * path, not a defect to count. The hook's existing no-route error keeps its
 * own context because it carries a different payload (`authoredRoutes`).
 *
 * Object argument, not positional: the five fields were two `string[]`,
 * one union, two strings. A swap of `types` and `knownRuleTypes`, or of
 * `slug` and `summary`, typechecks and mislabels the Datadog facets —
 * review of #812 (2026-10-06). The call sites now name the facets.
 */
interface ReasonedRejectionArgs {
  types: string[]
  knownRuleTypes: string[]
  reason: CheckoutRuleErrorReason
  slug: string | undefined
  summary: string
}

/**
 * Object argument so the two fields cannot be transposed by a caller — the
 * same treatment `reportReasonedRejection` got on review of #812, carried
 * over on review of COM-1195 (2026-10-07). The two fields are a distinct
 * shape so a positional swap would have typechecked, which is the whole
 * reason for naming them at the call site.
 */
interface RedirectToUnmetRuleArgs {
  types: string[]
  reason?: CheckoutRuleErrorReason
}

const reportReasonedRejection = ({
  types,
  knownRuleTypes,
  reason,
  slug,
  summary,
}: ReasonedRejectionArgs) => {
  const context = generateDataDogContext({
    title: `checkout rule rejection: ${reason}`,
    extraInfo: {
      function: FUNCTION_NAME,
      reason,
      ruleTypes: JSON.stringify(types),
      knownRuleTypes: JSON.stringify(knownRuleTypes),
      slug: JSON.stringify(slug ?? null),
    },
  })
  datadogRum.startView(context.viewName)
  datadogRum.addError(
    new Error(
      `${summary} (reason=${reason}, ruleTypes=${JSON.stringify(types)})`,
    ),
    context,
  )
}

/**
 * Turning a checkout-rule rejection into a redirect rather than a dead end.
 *
 * The whole of COM-950's behaviour, and the narrow half of
 * `useCheckoutRuleRecovery` below. Two callers reach it from opposite
 * directions: `useCheckoutRulePreflight` asks the engine on cart entry and
 * routes on what comes back, and `useOrderConfirmation` routes on what a
 * `createOrder` rejection named. Both end at `IConfig.checkoutRuleRoutes`, the
 * storefront's map from a rule type to the screen that satisfies it.
 *
 * **Separate from `useCheckoutRuleRecovery` because it reads no state at
 * render time**, and both callers are mounted somewhere that makes that
 * matter. `useOrderConfirmation` is mounted by `EventManager` on *every*
 * container, so a `useAppSelector` here would subscribe every screen in the
 * app to `lastAppliedRules` for a value neither caller reads.
 *
 * **It acts only on rule types the storefront authored a screen for.** The
 * engine is not inert: cart-condition rules (minimum amount, maximum
 * quantity) are enforced today and come back `blocks && !checked` whenever
 * the cart misses them. A rule with no `checkoutRuleRoutes` entry is outside
 * this feature, and the redirect says nothing about it — see the guard above
 * the no-route branch.
 */
export const useCheckoutRuleRedirect = () => {
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const navigate = useNavigate()

  /**
   * Sends the shopper to the screen that fixes the first routable rule named,
   * and answers which one that was.
   *
   * Returns the rule type on a redirect and `null` otherwise, so a caller that
   * needs to remember where it sent them — `useCheckoutRulePreflight` and its
   * loop guard — does not have to repeat the route resolution to find out.
   *
   * **Truthy if and only if `navigate` was called**, which the pre-flight's
   * `markRecoveryRedirect` relies on: a rule type returned without a redirect
   * would mark a screen the shopper never saw.
   *
   * **A redirect also clears the place-order flow (COM-1358)**, so a push onto
   * `pushAfter` held behind the `CREATE_ORDER` loader cannot follow the
   * shopper off the rule screen. The clear sits here, right before
   * `navigate`, rather than with a caller reading the return value: the
   * cancellation then depends on no contract, and it has already landed if
   * `navigate` throws. Every `return null` leaves the flow alone, which is
   * what lets a non-redirecting `createOrder` failure reach the `failed`
   * screen. The full race is the COM-1358 bullet in `.claude/CLAUDE.md`.
   *
   * **The first routable type wins.** A rejection names every rule that
   * failed, and a shopper stands on one screen. Ordering is the caller's:
   * `getFailedCheckoutRules` preserves the server's, and the pre-flight
   * preserves the engine's.
   *
   * **Deliberately unlatched.** The loop guard lives in the pre-flight, the
   * only caller that can re-enter this on a screen the shopper did not ask
   * for. A rejection that repeats is a shopper who tried again, and they are
   * owed the redirect a second time.
   *
   * **Config is read through `useAppStore`, not `useAppSelector`.** Both
   * callers run this from a callback a network round trip after its closure
   * was built — Apollo's `onCompleted`, and the `catch` around `createOrder` —
   * so a render-time snapshot is as old as the request. COM-974's rule.
   */
  const redirectToUnmetRule = ({
    types,
    reason,
  }: RedirectToUnmetRuleArgs): CheckoutRules | null => {
    const { config, storeName, sessionId } = store.getState().defaultSlice
    const ruleTypes = types.filter(isCheckoutRule)

    /*
     * Nothing named is a checkout rule *and* the reason is one that only
     * navigates, so this feature has nothing to say.
     *
     * The guard is **after** the filter, not before it. Returning only on an
     * empty input would let a rejection carrying joi's validation messages
     * fall through to the toast below and tell the shopper their order was
     * blocked by a rule that does not exist.
     *
     * The `reason === undefined || reason === 'required_selection_missing'`
     * conjunct is forward-compat and twofold (review of COM-1195, 2026-10-07
     * — the first pass omitted the `required_selection_missing` disjunct and
     * dropped it into the no-route branch). Both arms navigate and report
     * nothing on their own, so an empty `ruleTypes` has no toast or Datadog
     * line to carry. The *other* known reasons the arms below handle
     * (`selection_not_offered`, `addon_failed`, `addon_invalid_response`,
     * every other generic reason) all have a toast and a Datadog line that
     * the arms raise on an empty list; falling through there is deliberate
     * because the empty-list case is a signal we want to see.
     *
     * **Live, and on the commonest path there is** (corrected for COM-1358;
     * an earlier revision called it defensive and unreachable). The empty
     * list does not come from the filter above but from upstream:
     * `getFailedCheckoutRules` returns `ruleTypes: []` and no `reason` for
     * every `createOrder` failure that names no rule — a network error, a
     * decline, a joi `BAD_REQUEST` — so all of them land here from
     * `useOrderConfirmation`'s catch. The pre-flight reaches it too, whenever
     * its `unmet` list comes out empty. Since COM-1358 returning here, before
     * the clear beside `navigate`, is what lets those shoppers through to the
     * confirmation screen's `failed` state rather than stranding them on the
     * cart. Kill case:
     * `still pushes onto pushAfter when createOrder fails for a non-rule
     * reason` in `routes/tests/checkoutRuleRejectionPushAfter.test.tsx`.
     */
    if (
      !ruleTypes.length &&
      (reason === undefined || reason === 'required_selection_missing')
    ) {
      return null
    }

    /*
     * Suppresses the no-route branch's toast when a reason arm already
     * dispatched its own. Review of COM-1195 (2026-10-07): on the no-route
     * fleet (everyone until COM-951), the `selection_not_offered` arm's
     * stale-pick toast was followed by the no-route branch's
     * `checkoutRuleRouteMissing` toast, stacking two visual toasts over the
     * shopper. One toast per rejection is the right shape; the duplicate
     * Datadog report is kept because the two reports describe the same
     * fault from different angles (the arm's reason facet and the no-route
     * branch's `authoredRoutes` facet).
     */
    let handledToast = false

    /*
     * `reason` (COM-1069) decides whether this is a redirect at all.
     *
     * - `undefined` and `required_selection_missing` → navigate, the default
     *   that both pre-COM-1069 storefronts and the natural rejection reach.
     * - `selection_not_offered` → the stored pick is stale. Clear it, raise
     *   a toast, **then fall through to the route lookup** so the picker
     *   mounts and refetches a fresh list. The only caller passing `reason`
     *   is `useOrderConfirmation`'s createOrder catch. Since COM-1111 that
     *   runs *before* `usePlaceOrderFlow` pushes to the confirmation route
     *   rather than after it, and only a redirect cancels that push
     *   (COM-1358) — so stopping here (as a prior revision did) still ends
     *   with the shopper on a failed confirmation panel with no way to
     *   re-pick, and `clearSelection`
     *   alone does not refetch anything since it leaves `available`
     *   untouched; only navigating to the picker re-mounts
     *   `useCheckoutRuleOptions`. Review of #812 (2026-10-06).
     * - `addon_failed` / `addon_invalid_response` → the storefront's ERP
     *   add-on is down. The retry affordance is the storefront's, not
     *   cinnamon's; a redirect sends the shopper to a picker that will fail
     *   the same way.
     * - every other reason → toast + Datadog, no redirect. These are
     *   authoring drifts or session timeouts the shopper cannot act on by
     *   choosing again on a picker.
     *
     * The branches that `return null` run *before* the route lookup, so an
     * unknown route for a reason-handled rule does not fall through to the
     * no-route branch below.
     */
    if (reason === 'selection_not_offered') {
      ruleTypes
        .filter(isDocumentRule)
        .forEach((ruleType) => dispatch(clearSelection({ ruleType })))

      const message = getAlertErrorMessage(
        AlertMessageType.CheckoutRuleSelectionNotOffered,
        config?.texts,
      )
      if (message) {
        dispatch(addAlert({ message }))
        handledToast = true
      }

      reportReasonedRejection({
        types,
        knownRuleTypes: ruleTypes,
        reason,
        slug: storeName,
        summary:
          'the stored checkout-rule pick was dropped as no longer offered; falling through to the picker route',
      })
      /*
       * Nothing to navigate to without a rule type, and the no-route branch
       * below would otherwise double-report. The toast + Datadog line we
       * just dispatched is this shopper's whole surface.
       */
      if (!ruleTypes.length) return null
      // Deliberate fall-through: proceed to the route lookup below so the
      // picker mounts and `useCheckoutRuleOptions` refetches on mount. If
      // no route is authored, the no-route branch below runs instead and
      // reports a second Datadog line — acceptable since each line describes
      // the same fault from a different angle (reason facet vs
      // `authoredRoutes` facet). `handledToast` keeps that branch from
      // *also* raising `checkoutRuleRouteMissing` on top of the stale-pick
      // toast — review of COM-1195 (2026-10-07).
    }

    if (reason === 'addon_failed' || reason === 'addon_invalid_response') {
      const message = getAlertErrorMessage(
        AlertMessageType.CheckoutRuleAddonFailed,
        config?.texts,
      )
      if (message) dispatch(addAlert({ message }))

      reportReasonedRejection({
        types,
        knownRuleTypes: ruleTypes,
        reason,
        slug: storeName,
        summary:
          'the checkout-rule add-on failed and cinnamon has no retry affordance',
      })
      return null
    }

    if (
      reason !== undefined &&
      reason !== 'required_selection_missing' &&
      reason !== 'selection_not_offered'
    ) {
      const message = getAlertErrorMessage(
        AlertMessageType.CheckoutRuleGenericRejection,
        config?.texts,
      )
      if (message) dispatch(addAlert({ message }))

      reportReasonedRejection({
        types,
        knownRuleTypes: ruleTypes,
        reason,
        slug: storeName,
        summary: 'a checkout-rule rejection fell through to the generic branch',
      })
      return null
    }

    /*
     * Narrow to the two picker rule types before trying to resolve a path.
     * `checkoutRuleRoutes` is typed `Partial<Record<CheckoutRules, string>>` on
     * purpose (see `interfaces/config.ts` for why: a rejection can name any
     * rule the engine enforces), so a storefront **could** author
     * `checkoutRuleRoutes['cartConditionPendingMinAmount'] = '/some/path'`,
     * the resolve would find it, and the shopper would be sent to a screen
     * that cannot fix a cart-minimum violation. No picker is a correct
     * destination for a cart-condition rule: adding products is, and that
     * belongs on the cart itself. The filter closes that case without a type
     * change at the config layer.
     *
     * ⚠️ **Local to the resolve, not the top-level `ruleTypes`.** Two
     * downstream readers (`authoredRoutes` for the Datadog payload, every
     * reason-arm's `knownRuleTypes` facet) have legitimate reasons to see
     * cart-condition members. Narrowing globally would hide them from the
     * operator-side record exactly when they are the signal the record
     * exists to carry. (The `selection_not_offered` arm's `clearSelection`
     * loop already applies its own `.filter(isDocumentRule)` before this
     * runs, so it never sees cart-condition members either way.)
     */
    const resolved = ruleTypes.filter(isDocumentRule).reduce<
      { ruleType: CheckoutRules; path: string } | undefined
    >((found, ruleType) => {
      if (found) return found
      const path = resolveCheckoutRuleRoute(
        config?.checkoutRuleRoutes?.[ruleType],
        config?.routes,
        storeName,
        sessionId,
      )
      return path ? { ruleType, path } : undefined
    }, undefined)

    if (resolved) {
      /*
       * `replace`, never `usePushView`, and this is a security requirement
       * rather than a Back-button preference.
       *
       * Both halves of the path come from the URL, and a slug carrying `%2f`
       * decodes to `//evil.com`. `history.replace` calls `replaceState` bare,
       * so the browser's same-origin check throws and nothing navigates;
       * `history.push` swallows that and falls back to
       * `window.location.assign`, which navigates cross-origin. Verified
       * against the installed `@remix-run/router@1.21.0`, and spelled out at
       * length in `templates/OrderConfirmation.tsx`.
       *
       * It reads correctly for the shopper too. The URL they are leaving is a
       * cart that will not advance, or a confirmation screen for an order
       * that does not exist. Neither is worth a Back button.
       */
      dispatch(clearPlaceOrderActions())
      navigate(resolved.path, { replace: true })
      return resolved.ruleType
    }

    const authoredRoutes = ruleTypes.reduce<Record<string, string>>(
      (entries, ruleType) => {
        const authored = config?.checkoutRuleRoutes?.[ruleType]
        return authored ? { ...entries, [ruleType]: authored } : entries
      },
      {},
    )

    /*
     * None of the picker rules has an authored screen, so none of them is this
     * feature's to handle — stay silent.
     *
     * The gate decides on the picker-filtered subset, not the full
     * `authoredRoutes`. The full map is kept for the Datadog payload below
     * (operator visibility, per COM-736), but a cart-condition entry
     * authored into `checkoutRuleRoutes` must **not** keep the silent gate
     * from firing — doing so would restore the `unilever-ecu-hpc` toast
     * (`checkoutRuleRouteMissing`, "return to the conversation") over a cart
     * the shopper can fix by adding products. #822 review (COM-1212) caught
     * this: filtering only the resolve left the toast reachable on the
     * misauthoring case, which is exactly the shape the fix was meant to
     * close.
     *
     * Silent branch covers: every storefront that has not opted into the
     * pickers, and every rule an opted-in one did not author a picker
     * screen for — the cart-condition rules above all (minimum amount,
     * maximum quantity), which are enforced today and which the cart never
     * redirected or toasted for before this feature. A `createOrder`
     * rejection already records its failure on the slice before calling
     * this, so the confirmation screen still says the order failed.
     */
    const authoredPickerRoutes = Object.keys(authoredRoutes).filter(
      (ruleType): ruleType is CheckoutRuleDocumentType =>
        isDocumentRule(ruleType as CheckoutRules),
    )
    if (!authoredPickerRoutes.length) return null

    /*
     * An authored entry that did not resolve: a token the storefront does not
     * recognise, or a path its own `routes` table does not register. An
     * authoring bug on an opted-in storefront, and the only no-route case
     * worth alerting on — so say so and leave the shopper where they are.
     *
     * Never a throw and never an error screen: the shopper's cart is intact.
     * The toast points at the conversation, which is the only place the order
     * can still be finished.
     *
     * `handledToast` suppresses the toast when a reason arm above already
     * raised its own — see the `selection_not_offered` fall-through for the
     * one case that reaches here with a toast already up. The Datadog line
     * below is unconditional: a second report from a different angle
     * (`authoredRoutes`) is useful even where the arm has already filed one.
     */
    const message = getAlertErrorMessage(
      AlertMessageType.CheckoutRuleRouteMissing,
      config?.texts,
    )
    if (message && !handledToast) dispatch(addAlert({ message }))

    const context = generateDataDogContext({
      title: 'checkout rule blocked the shopper with no screen to fix it',
      extraInfo: {
        function: FUNCTION_NAME,
        ruleTypes: JSON.stringify(types),
        knownRuleTypes: JSON.stringify(ruleTypes),
        authoredRoutes: JSON.stringify(authoredRoutes),
        slug: JSON.stringify(storeName ?? null),
      },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error(
        `checkout rules ${JSON.stringify(
          types,
        )} blocked the order and none resolved to a route; authored entries ${JSON.stringify(
          authoredRoutes,
        )}, so the shopper stays put and cannot satisfy the rule here`,
      ),
      context,
    )

    return null
  }

  return redirectToUnmetRule
}

/**
 * The redirect, plus the question a cart screen asks before offering to place
 * an order.
 *
 * The shape COM-950 specifies. Nothing in COM-950 reads
 * `hasUnmetBlockingRules` — both of its callers use `useCheckoutRuleRedirect`
 * above, because neither renders from it. The reader is COM-1045's *Change*
 * rows and COM-1046's *Continuar* CTA, and the predicate lives here rather
 * than in those tickets because it belongs beside the field it interprets.
 */
export const useCheckoutRuleRecovery = () => {
  const redirectToUnmetRule = useCheckoutRuleRedirect()
  const lastAppliedRules = useAppSelector(selectLastAppliedRules)

  /**
   * Whether the engine's last answer left a blocking rule unsatisfied.
   *
   * `blocks && !checked`, not `resultCheck`: the top-level flag answers for
   * the whole session, and a shopper can only be sent to one screen, so the
   * per-rule pair is the one that says *which*.
   *
   * `undefined` — no pre-flight has run — answers `false`. A cart that has
   * not asked must not claim to know that something blocks it.
   */
  const hasUnmetBlockingRules = (lastAppliedRules ?? []).some(
    (rule) => rule.blocks && !rule.checked,
  )

  return { redirectToUnmetRule, hasUnmetBlockingRules }
}

export default useCheckoutRuleRecovery
